import type { AppUser, CampingSpot, VehicleCategory } from '../types';

export interface VehicleVisitEntry {
  category: VehicleCategory;
  count: number;
}

export interface CreatorVehicleInfo {
  category: VehicleCategory;
  lengthCm?: number;
  is4wd?: boolean;
}

export interface UserVehicleMatch {
  category?: VehicleCategory;
  lengthCm?: number;
  sameCategory: boolean;
  fitsMaxLength: boolean;
}

export interface VehicleVisitsSummary {
  entries: VehicleVisitEntry[];
  maxLengthCm: number | null;
  checkInCount: number;
  checkInNights: number;
  creatorVehicle?: CreatorVehicleInfo;
  userMatch?: UserVehicleMatch;
}

const VEHICLE_CATEGORIES: VehicleCategory[] = [
  'motorhome', 'campervan', 'caravan', 'camper_trailer', 'rooftent', 'offroad', 'car',
];

/** Kan overlay åpnes? Minst netter/innsjekk eller kjøretøydata. */
export function canOpenVehicleVisitsOverlay(spot: Pick<CampingSpot, 'checkInCount' | 'checkInNights' | 'vehicleVisits' | 'vehicleMaxLength' | 'creatorVehicleCategory'>): boolean {
  const nights = spot.checkInNights ?? 0;
  const count = spot.checkInCount ?? 0;
  if (nights > 0 || count > 0) return true;
  if ((spot.vehicleMaxLength ?? 0) > 0) return true;
  if (spot.creatorVehicleCategory) return true;
  if (spot.vehicleVisits && Object.values(spot.vehicleVisits).some(c => c > 0)) return true;
  return false;
}

export function buildVehicleVisitsSummary(
  spot: Pick<
    CampingSpot,
    'vehicleVisits' | 'vehicleMaxLength' | 'checkInCount' | 'checkInNights' |
    'creatorVehicleCategory' | 'creatorVehicleLength' | 'creatorVehicleIs4wd'
  >,
  user?: Pick<AppUser, 'activeVehicleCategory' | 'activeVehicleLength'> | null,
): VehicleVisitsSummary {
  const entries: VehicleVisitEntry[] = [];
  if (spot.vehicleVisits) {
    for (const cat of VEHICLE_CATEGORIES) {
      const count = spot.vehicleVisits[cat] ?? 0;
      if (count > 0) entries.push({ category: cat, count });
    }
    // Ukategoriserte nøkler (legacy/ukjent)
    for (const [cat, count] of Object.entries(spot.vehicleVisits)) {
      if (count > 0 && !VEHICLE_CATEGORIES.includes(cat as VehicleCategory)) {
        entries.push({ category: cat as VehicleCategory, count });
      }
    }
    entries.sort((a, b) => b.count - a.count);
  }

  const maxLengthCm = (spot.vehicleMaxLength ?? 0) > 0 ? spot.vehicleMaxLength! : null;
  const creatorVehicle = spot.creatorVehicleCategory
    ? {
        category: spot.creatorVehicleCategory,
        lengthCm: spot.creatorVehicleLength,
        is4wd: spot.creatorVehicleIs4wd,
      }
    : undefined;

  let userMatch: UserVehicleMatch | undefined;
  const userCat = user?.activeVehicleCategory;
  const userLen = user?.activeVehicleLength;
  if (userCat || userLen) {
    const categoriesSeen = new Set(entries.map(e => e.category));
    if (creatorVehicle) categoriesSeen.add(creatorVehicle.category);
    const sameCategory = userCat ? categoriesSeen.has(userCat) : false;
    const fitsMaxLength = userLen && maxLengthCm ? userLen <= maxLengthCm : !maxLengthCm;
    userMatch = {
      category: userCat,
      lengthCm: userLen,
      sameCategory,
      fitsMaxLength: fitsMaxLength ?? true,
    };
  }

  return {
    entries,
    maxLengthCm,
    checkInCount: spot.checkInCount ?? 0,
    checkInNights: spot.checkInNights ?? 0,
    creatorVehicle,
    userMatch,
  };
}
